import React, { useMemo, useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Image, Form, Spinner, Badge, Modal, Button } from "react-bootstrap";
import NavbarFacach from "../components/NavbarFacach";
import FooterFacach from "../components/FooterFacach";
import SocialBar from "../components/SocialBar";
import logoFacach from "../assets/fotos/ISOLOO.png";
import "../components/memorias.css";
import { useSiteSettings } from '../hooks/useSiteSettings';
import fallbackBanner from '../assets/imagen.jpg';
import * as pdfjsLib from 'pdfjs-dist';

// Worker de pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const API_URL = "https://facach.coop.hn/api";

const FALLBACK_DOCS = [
  { id: 1, title: "Memoria Institucional 2024", year: 2024, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf", file_size_bytes: null },
  { id: 2, title: "Memoria Institucional 2023", year: 2023, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf", file_size_bytes: null },
  { id: 3, title: "Memoria Institucional 2022", year: 2022, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf", file_size_bytes: null },
];

function formatSize(bytes) {
  if (!bytes) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

// ── Componente que renderiza la carátula del PDF ──────────────────────────────
function PdfCover({ url, title }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!url) { setError(true); setLoading(false); return; }
    let cancelled = false;

    const render = async () => {
      try {
        const pdf  = await pdfjsLib.getDocument({ url, withCredentials: false }).promise;
        const page = await pdf.getPage(1);
        if (cancelled) return;

        const canvas  = canvasRef.current;
        if (!canvas) return;
        const ctx     = canvas.getContext('2d');
        const viewport = page.getViewport({ scale: 1.2 });

        canvas.width  = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) setLoading(false);
      } catch (e) {
        console.warn('PDF cover error:', e.message);
        if (!cancelled) { setError(true); setLoading(false); }
      }
    };

    render();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div className="libro-cover-placeholder">
        <Image src={logoFacach} alt={title} className="libro-placeholder-logo" />
        <div className="libro-placeholder-title">{title}</div>
      </div>
    );
  }

  return (
    <div className="libro-canvas-wrap">
      {loading && (
        <div className="libro-cover-loading">
          <Spinner animation="border" size="sm" variant="light" />
        </div>
      )}
      <canvas ref={canvasRef} className="libro-canvas" style={{ opacity: loading ? 0 : 1 }} />
    </div>
  );
}

// ── Tarjeta de libro ──────────────────────────────────────────────────────────
function LibroCard({ doc, onVerMas }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`libro-card ${hovered ? 'libro-card--hovered' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onVerMas(doc)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onVerMas(doc)}
      aria-label={`Ver ${doc.title}`}
    >
      {/* Lomo del libro */}
      <div className="libro-lomo">
        <span className="libro-lomo-text">{doc.year}</span>
      </div>

      {/* Portada */}
      <div className="libro-portada">
        <PdfCover url={doc.file_url} title={doc.title} />

        {/* Overlay al hover */}
        <div className="libro-overlay">
          <div className="libro-overlay-content">
            
            <div className="libro-overlay-text">Ver documento</div>
          </div>
        </div>
      </div>

      {/* Sombra del libro */}
      <div className="libro-sombra" />
    </div>
  );
}

// ── Modal de detalle ──────────────────────────────────────────────────────────
function LibroModal({ doc, onHide }) {
  if (!doc) return null;
  return (
    <Modal show={!!doc} onHide={onHide} centered size="md">
      <Modal.Header closeButton style={{ borderBottom: "3px solid #1a3a6b" }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 800, color: "#1a3a6b" }}>
          {doc.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          {/* Mini preview */}
          <div style={{ width: 120, flexShrink: 0 }}>
            <PdfCover url={doc.file_url} title={doc.title} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 12 }}>
              <Badge bg="primary" style={{ marginRight: 8 }}>PDF</Badge>
              <Badge bg="secondary">{doc.year}</Badge>
            </div>
            <div style={{ fontSize: 13, color: "#7a8aaa", marginBottom: 16 }}>
              {formatSize(doc.file_size_bytes) && (
                <span><strong>Tamaño:</strong> {formatSize(doc.file_size_bytes)}</span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <a href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-sm"
                style={{ textAlign: "center" }}>Ver documento</a>
                
              <a
          
                href={doc.file_url}
                download
                className="btn btn-outline-primary btn-sm"
                style={{ textAlign: "center" }}
              >
              Descargar PDF
              </a>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
const Memoria = () => {
  const { settings } = useSiteSettings();
  const bannerSrc = settings.banner_image
    ? `https://facach.coop.hn${settings.banner_image}`
    : fallbackBanner;

  const [documentos, setDocumentos] = useState(FALLBACK_DOCS);
  const [loading, setLoading]       = useState(true);
  const [query, setQuery]           = useState("");
  const [anioFiltro, setAnioFiltro] = useState("todos");
  const [selected, setSelected]     = useState(null);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const res  = await fetch(`${API_URL}/documents/institutional_reports`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.ok && json.data?.length > 0) setDocumentos(json.data);
      } catch (err) {
        console.error("Error cargando memorias:", err.message);
      } finally { setLoading(false); }
    };
    fetchDocs();
  }, []);

  const aniosDisponibles = useMemo(() => {
    const set = new Set(documentos.map(d => d.year));
    return Array.from(set).sort((a, b) => b - a);
  }, [documentos]);

  const docsFiltrados = useMemo(() => {
    return documentos.filter(d => {
      const coincideTexto = d.title.toLowerCase().includes(query.trim().toLowerCase());
      const coincideAnio  = anioFiltro === "todos" ? true : String(d.year) === String(anioFiltro);
      return coincideTexto && coincideAnio;
    });
  }, [documentos, query, anioFiltro]);

  return (
    <Container fluid className="p-0 memorias-page">
      <NavbarFacach />

      {/* Banner */}
      <div className="banner-wrapper" aria-label="Encabezado de Memorias Institucionales">
        <Image src={bannerSrc} alt="Memorias Institucionales - FACACH" fluid className="banner-image" />
        <div className="banner-overlay">
          <h1 className="banner-title">MEMORIAS INSTITUCIONALES</h1>
          <p className="banner-description">
            En esta sección encontrarás las memorias institucionales disponibles para consulta y descarga.
          </p>
        </div>
        
      </div>

      <Container className="mt-4">

        {/* Filtros */}
        <section className="filtros-section" aria-label="Filtros de búsqueda">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={8}>
              <Form.Group controlId="buscarMemoria">
                <Form.Label className="filtro-label">Buscar por título</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej.: Memoria 2024"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group controlId="filtrarAnio">
                <Form.Label className="filtro-label">Año</Form.Label>
                <Form.Select value={anioFiltro} onChange={e => setAnioFiltro(e.target.value)}>
                  <option value="todos">Todos</option>
                  {aniosDisponibles.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </section>

        {/* Documentos */}
        <section className="documentos-section">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : docsFiltrados.length === 0 ? (
            <Row className="justify-content-center">
              <Col xs={12} md={10}>
                <Card className="plan-card empty-card">
                  <Card.Body className="text-center">
                    <Image src={logoFacach} alt="FACACH" className="icon-facach-empty" />
                    <h5 className="empty-title">Sin resultados</h5>
                    <p className="empty-text">
                      No encontramos documentos que coincidan con tus filtros.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <div className="libros-grid">
              {docsFiltrados.map(doc => (
                <div key={doc.id} className="libros-grid-item">
                  <LibroCard doc={doc} onVerMas={setSelected} />
                  <div className="libro-info">
                    <div className="libro-titulo">{doc.title}</div>
                    <div className="libro-anio">{doc.year}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </Container>

      <SocialBar position="right" top="35%" size={42} rounded={true} />
      <FooterFacach />

      <LibroModal doc={selected} onHide={() => setSelected(null)} />
    </Container>
  );
};

export default Memoria;